import React from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import { FaGithub, FaXTwitter, FaEnvelope, FaTelegram } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import NewsletterForm from "../Components/NewsletterForm";

const TELEGRAM_URL = "https://t.me/+IPYp6q06dWsyZjY0";

const Contact = () => {
	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const isRtl = i18n.language === "fa" || i18n.language === "farshi";

	const socialLinks = [
		{
			icon: FaGithub,
			url: "https://github.com/smswithoutborders",
			label: t("ContactUs.Items.GitHub.Label", "GitHub"),
			description: t(
				"ContactUs.Items.GitHub.Description",
				"Explore our open-source projects and repositories."
			),
			ButtonText: t("ContactUs.Items.GitHub.ButtonText", "View GitHub")
		},
		{
			icon: FaXTwitter,
			url: "https://x.com/RelaySMS",
			label: t("ContactUs.Items.Twitter.Label", "Twitter"),
			description: t(
				"ContactUs.Items.Twitter.Description",
				"Follow us for updates, tips, and dev thoughts."
			),
			ButtonText: t("ContactUs.Items.Twitter.ButtonText", "Follow on Twitter")
		},
		{
			icon: FaEnvelope,
			url: "mailto:developers@smswithoutborders.com",
			label: t("ContactUs.Items.Email.Label", "Email"),
			description: t(
				"ContactUs.Items.Email.Description",
				"Reach out directly via email for collaboration and questions."
			),
			ButtonText: t("ContactUs.Items.Email.ButtonText", "Send Email")
		}
	];

	return (
		<Box
			dir={isRtl ? "rtl" : "ltr"}
			sx={{
				position: "relative",
				overflow: "hidden",
				fontFamily: "Montserrat, sans-serif",
				textAlign: "center",
				px: 2
			}}
		>
			<Box
				sx={{
					zIndex: 2,
					position: "relative",
					minHeight: "85vh",
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
					color: "#000",
					maxWidth: "1200px",
					mx: "auto",
					pt: { xs: "120px", md: "140px" }
				}}
			>
				<Typography
					variant="h2"
					sx={{
						mb: { xs: 6, md: 8 },
						fontSize: { md: "2rem", xs: "1.5rem" },
						color: "text.primary",
						fontWeight: 700,
						fontFamily: "'Unbounded', Ubuntu",
						mt: { xs: 6, md: 0 }
					}}
				>
					{t("ContactUs.Title", "Contact RelaySMS Team")}
				</Typography>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							sm: "repeat(2, 1fr)",
							md: "repeat(3, 1fr)"
						},
						gap: 4,
						width: "100%",
						px: { xs: 2, sm: 4 }
					}}
				>
					{socialLinks.map(({ icon, url, label, description, ButtonText }, index) => (
						<Box
							key={index}
							sx={{
								borderRadius: "6px",
								padding: 3,
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-start",
								textAlign: "left",
								transition: "transform 0.3s ease, box-shadow 0.3s ease",
								border: "1px solid",
								borderColor: "divider",
								"&:hover": {
									transform: "translateY(-4px)",
									boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)"
								}
							}}
						>
							{React.createElement(icon, {
								style: {
									fontSize: "1.6rem",
									color: theme.palette.text.primary,
									marginBottom: "2.5rem"
								}
							})}
							<Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}>
								{label}
							</Typography>
							<Typography
								variant="body2"
								sx={{ mb: 3, color: "text.secondary", lineHeight: 1.6, flex: 1 }}
							>
								{description}
							</Typography>
							<Button
								href={url || "#"}
								target={url.startsWith("mailto:") ? undefined : "_blank"}
								rel={url.startsWith("mailto:") ? undefined : "noopener noreferrer"}
								sx={{
									color: "text.primary",
									textTransform: "none",
									fontWeight: 400,
									fontSize: "15px",
									padding: 0,
									textDecoration: "underline",
									textUnderlineOffset: "4px",
									textDecorationThickness: "2px",
									transition: "all 0.3s ease",
									"&:hover": {
										color: "text.primary",
										textDecorationColor: "primary.main",
										backgroundColor: "transparent"
									}
								}}
							>
								{ButtonText}
							</Button>
						</Box>
					))}
				</Box>
			</Box>
			{/* ── Mailing List ── */}
			<Box
				sx={{
					mt: { xs: 6, md: 2 },
					mx: { xs: 3, md: 6 },
					mb: { xs: 6, md: 10 },
					borderRadius: 4,
					py: { xs: 6, md: 8 },
					px: { xs: 3, md: 8 },
					textAlign: "center",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 3
				}}
			>
				<NewsletterForm showHeading={true} align="center" sx={{ width: "100%", maxWidth: 800 }} />
			</Box>
			
			{/* ── Join the Community ── */}
			<Box
				sx={{
					mt: { xs: 6, md: 2 },
					mx: { xs: 3, md: 6 },
					mb: { xs: 6, md: 10 },
					borderRadius: 4,
					py: { xs: 6, md: 8 },
					px: { xs: 3, md: 8 },
					textAlign: "center",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 3
				}}
			>
				<Typography
					variant="h4"
					sx={{
						fontSize: { xs: "1.5rem", md: "1.9rem" },
						fontWeight: 800,
						fontFamily: "'Unbounded', Ubuntu",
						color: "text.primary",
						lineHeight: 1.25
					}}
				>
					{t("Blog.communityTitle")}
				</Typography>
				<Typography
					sx={{
						fontSize: { xs: "0.95rem", md: "1.05rem" },
						color: "text.secondary",
						fontFamily: "Ubuntu",
						maxWidth: 520,
						lineHeight: 1.75
					}}
				>
					{t("Blog.communityDescription")}
				</Typography>
				<Button
					component="a"
					href={TELEGRAM_URL}
					target="_blank"
					rel="noopener noreferrer"
					variant="contained"
					startIcon={<FaTelegram size={18} />}
					sx={{
						bgcolor: "primary.main",
						color: "#ffffff",
						fontFamily: "Ubuntu",
						fontWeight: 600,
						fontSize: "1rem",
						textTransform: "none",
						borderRadius: 2,
						px: 4,
						py: 1.5,
						"&:hover": { bgcolor: "primary.dark" }
					}}
				>
					{t("Blog.joinTelegram")}
				</Button>
			</Box>
		</Box>
	);
};

export default Contact;
